import * as THREE from 'three';
import {
  Scene,
  Color,
  Fog,
  FogExp2,
  DirectionalLight,
  PointLight,
  SpotLight,
  AmbientLight,
  HemisphereLight,
  RectAreaLight,
  Group,
  Mesh,
  PlaneGeometry,
  MeshStandardMaterial,
  MeshPhysicalMaterial,
  Texture,
  CubeTexture,
  PMREMGenerator,
  Vector3,
  Euler,
  Object3D,
} from 'three';
import { RGBELoader } from 'three-stdlib';

/**
 * 조명 타입 정의
 */
export type LightType = 'directional' | 'point' | 'spot' | 'ambient' | 'hemisphere' | 'rectArea';

/**
 * 조명 정보 인터페이스
 */
export interface LightInfo {
  id: string;
  name: string;
  light: THREE.Light;
  type: LightType;
  position: Vector3;
  rotation: Euler;
  intensity: number;
  color: Color;
  visible: boolean;
  castShadow: boolean;
  userData: any;
}

/**
 * 환경 설정 인터페이스
 */
export interface EnvironmentSettings {
  // HDR 환경맵
  hdrUrl?: string;
  hdrIntensity: number;

  // 안개 설정
  fogEnabled: boolean;
  fogType: 'linear' | 'exponential';
  fogColor: string | number;
  fogNear: number;
  fogFar: number;
  fogDensity: number;

  // 배경 설정
  backgroundColor: string | number;
  backgroundIntensity: number;

  // 그림자 설정
  shadowEnabled: boolean;
  shadowType: 'PCFSoftShadowMap' | 'PCFShadowMap' | 'BasicShadowMap';
  shadowBias: number;
  shadowNormalBias: number;

  // 바닥 설정
  floorEnabled: boolean;
  floorColor: string | number;
  floorRoughness: number;
  floorMetalness: number;
  floorSize: number;
  floorPosition: Vector3;
  floorReceiveShadow: boolean;

  // 전역 조명 설정
  ambientLightEnabled: boolean;
  ambientLightColor: string | number;
  ambientLightIntensity: number;

  // 반사 설정
  reflectionEnabled: boolean;
  reflectionIntensity: number;
  reflectionRoughness: number;
}

/**
 * HDR, 조명, 바닥, 그림자, 안개 등 모든 환경 요소를 제어하는 클래스
 */
export class EnvironmentControl {
  // Scene 참조
  private _scene: Scene | null = null;

  // 환경 요소 그룹
  private _environmentGroup: Group;
  private _lightsGroup: Group;
  private _floorGroup: Group;

  // 조명 관리
  private _lights: Map<string, LightInfo> = new Map();
  private _lightCounter: number = 0;

  // HDR 환경맵
  private _hdrTexture: Texture | CubeTexture | null = null;
  private _pmremGenerator: PMREMGenerator | null = null;

  // 환경 설정
  private _settings: EnvironmentSettings;

  // 이벤트 콜백
  private _onEnvironmentChanged?: (settings: EnvironmentSettings) => void;
  private _onLightAdded?: (lightInfo: LightInfo) => void;
  private _onLightRemoved?: (lightId: string) => void;

  constructor() {
    // 환경 그룹 초기화
    this._environmentGroup = new THREE.Group();
    this._environmentGroup.name = 'EnvironmentGroup';

    this._lightsGroup = new THREE.Group();
    this._lightsGroup.name = 'LightsGroup';

    this._floorGroup = new THREE.Group();
    this._floorGroup.name = 'FloorGroup';

    this._environmentGroup.add(this._lightsGroup);
    this._environmentGroup.add(this._floorGroup);

    // 기본 환경 설정
    this._settings = this.getDefaultSettings();
  }

  /**
   * Scene 설정
   */
  public setScene(scene: Scene): void {
    this._scene = scene;
    scene.add(this._environmentGroup);
    this.applyEnvironmentSettings();
  }

  /**
   * 기본 환경 설정 가져오기
   */
  private getDefaultSettings(): EnvironmentSettings {
    return {
      hdrIntensity: 0.68,
      fogEnabled: true,
      fogType: 'linear',
      fogColor: 0x7b7d83,
      fogNear: 10,
      fogFar: 100,
      fogDensity: 0.01,
      backgroundColor: 0x7b7d83,
      backgroundIntensity: 1.0,
      shadowEnabled: true,
      shadowType: 'PCFSoftShadowMap',
      shadowBias: -0.0001,
      shadowNormalBias: 0.02,
      floorEnabled: true,
      floorColor: 0x7b7d83,
      floorRoughness: 1.0,
      floorMetalness: 0.0,
      floorSize: 1000,
      floorPosition: new Vector3(0, -0.01, 0),
      floorReceiveShadow: true,
      ambientLightEnabled: true,
      ambientLightColor: 0x404040,
      ambientLightIntensity: 0.4,
      reflectionEnabled: false,
      reflectionIntensity: 0.5,
      reflectionRoughness: 0.5,
    };
  }

  /**
   * HDR 환경맵 로드
   */
  public async loadHDREnvironment(hdrUrl: string, intensity: number = 1.0): Promise<boolean> {
    if (!this._scene) return false;

    try {
      const loader = new RGBELoader();
      const texture = await loader.loadAsync(hdrUrl);

      if (this._pmremGenerator) {
        const envMap = this._pmremGenerator.fromEquirectangular(texture).texture;
        this._scene.environment = envMap;
        this._hdrTexture = envMap;
        this._settings.hdrUrl = hdrUrl;
        this._settings.hdrIntensity = intensity;

        // HDR 텍스처 해제
        texture.dispose();

        this.notifyEnvironmentChanged();
        return true;
      }
    } catch (error) {
      console.error('HDR 환경맵 로드 실패:', error);
    }

    return false;
  }

  /**
   * PMREM 생성기 설정
   */
  public setPMREMGenerator(generator: PMREMGenerator): void {
    this._pmremGenerator = generator;
  }

  /**
   * 방향성 조명 추가
   */
  public addDirectionalLight(
    position: Vector3 = new Vector3(5, 5, 5),
    target: Vector3 = new Vector3(0, 0, 0),
    color: string | number = 0xffffff,
    intensity: number = 1.0,
    name?: string,
  ): string {
    const light = new DirectionalLight(color, intensity);
    light.position.copy(position);
    light.target.position.copy(target);

    // 그림자 설정
    light.castShadow = true;
    light.shadow.mapSize.width = 2048;
    light.shadow.mapSize.height = 2048;
    light.shadow.camera.near = 0.5;
    light.shadow.camera.far = 500;
    light.shadow.camera.left = -50;
    light.shadow.camera.right = 50;
    light.shadow.camera.top = 50;
    light.shadow.camera.bottom = -50;

    return this.addLight(light, 'directional', name);
  }

  /**
   * 포인트 조명 추가
   */
  public addPointLight(
    position: Vector3 = new Vector3(0, 5, 0),
    color: string | number = 0xffffff,
    intensity: number = 1.0,
    distance: number = 0,
    decay: number = 2,
    name?: string,
  ): string {
    const light = new PointLight(color, intensity, distance, decay);
    light.position.copy(position);
    light.castShadow = true;

    return this.addLight(light, 'point', name);
  }

  /**
   * 스포트 조명 추가
   */
  public addSpotLight(
    position: Vector3 = new Vector3(0, 5, 0),
    target: Vector3 = new Vector3(0, 0, 0),
    color: string | number = 0xffffff,
    intensity: number = 1.0,
    distance: number = 0,
    angle: number = Math.PI / 3,
    penumbra: number = 0,
    decay: number = 2,
    name?: string,
  ): string {
    const light = new SpotLight(color, intensity, distance, angle, penumbra, decay);
    light.position.copy(position);
    light.target.position.copy(target);
    light.castShadow = true;

    return this.addLight(light, 'spot', name);
  }

  /**
   * 주변 조명 추가
   */
  public addAmbientLight(
    color: string | number = 0x404040,
    intensity: number = 0.4,
    name?: string,
  ): string {
    const light = new AmbientLight(color, intensity);
    return this.addLight(light, 'ambient', name);
  }

  /**
   * 반구 조명 추가
   */
  public addHemisphereLight(
    skyColor: string | number = 0x87ceeb,
    groundColor: string | number = 0x080820,
    intensity: number = 1.0,
    name?: string,
  ): string {
    const light = new HemisphereLight(skyColor, groundColor, intensity);
    return this.addLight(light, 'hemisphere', name);
  }

  /**
   * 사각 영역 조명 추가
   */
  public addRectAreaLight(
    color: string | number = 0xffffff,
    intensity: number = 1.0,
    width: number = 10,
    height: number = 10,
    name?: string,
  ): string {
    const light = new RectAreaLight(color, intensity, width, height);
    return this.addLight(light, 'rectArea', name);
  }

  /**
   * 조명 추가 (공통 메서드)
   */
  private addLight(light: THREE.Light, type: LightType, name?: string): string {
    const id = this.generateLightId();
    const lightName = name || `${type}_${id}`;

    const lightInfo: LightInfo = {
      id,
      name: lightName,
      light,
      type,
      position: light.position.clone(),
      rotation: light.rotation.clone(),
      intensity: light.intensity,
      color: light.color.clone(),
      visible: light.visible,
      castShadow: light.castShadow,
      userData: {},
    };

    this._lights.set(id, lightInfo);
    this._lightsGroup.add(light);

    if (this._onLightAdded) {
      this._onLightAdded(lightInfo);
    }

    return id;
  }

  /**
   * 조명 제거
   */
  public removeLight(lightId: string): boolean {
    const lightInfo = this._lights.get(lightId);
    if (!lightInfo) return false;

    this._lightsGroup.remove(lightInfo.light);
    this._lights.delete(lightId);

    if (this._onLightRemoved) {
      this._onLightRemoved(lightId);
    }

    return true;
  }

  /**
   * 조명 속성 설정
   */
  public setLightProperty(lightId: string, property: string, value: any): boolean {
    const lightInfo = this._lights.get(lightId);
    if (!lightInfo) return false;

    const light = lightInfo.light;
    if (property in light) {
      (light as any)[property] = value;

      // 위치나 회전이 변경된 경우 정보 업데이트
      if (property === 'position') {
        lightInfo.position.copy(value);
      } else if (property === 'rotation') {
        lightInfo.rotation.copy(value);
      } else if (property === 'intensity') {
        lightInfo.intensity = value;
      } else if (property === 'color') {
        lightInfo.color.copy(value);
      }

      return true;
    }

    return false;
  }

  /**
   * 바닥 생성
   */
  public createFloor(
    size: number = 1000,
    position: Vector3 = new Vector3(0, -0.01, 0),
    color: string | number = 0x7b7d83,
    roughness: number = 1.0,
    metalness: number = 0.0,
  ): void {
    if (!this._floorGroup) return;

    // 기존 바닥 제거
    this._floorGroup.clear();

    const geometry = new PlaneGeometry(size, size);
    const material = new MeshStandardMaterial({
      color,
      roughness,
      metalness,
      side: THREE.DoubleSide,
    });

    const floor = new Mesh(geometry, material);
    floor.position.copy(position);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.name = 'Floor';

    this._floorGroup.add(floor);

    // 설정 업데이트
    this._settings.floorEnabled = true;
    this._settings.floorColor = color;
    this._settings.floorRoughness = roughness;
    this._settings.floorMetalness = metalness;
    this._settings.floorSize = size;
    this._settings.floorPosition.copy(position);

    this.notifyEnvironmentChanged();
  }

  /**
   * 바닥 제거
   */
  public removeFloor(): void {
    if (this._floorGroup) {
      this._floorGroup.clear();
    }

    this._settings.floorEnabled = false;
    this.notifyEnvironmentChanged();
  }

  /**
   * 안개 설정
   */
  public setFog(
    enabled: boolean,
    type: 'linear' | 'exponential' = 'linear',
    color: string | number = 0x000000,
    near: number = 10,
    far: number = 100,
    density: number = 0.01,
  ): void {
    if (!this._scene) return;

    if (enabled) {
      if (type === 'linear') {
        this._scene.fog = new Fog(color, near, far);
      } else {
        this._scene.fog = new FogExp2(color, density);
      }
    } else {
      this._scene.fog = null;
    }

    // 설정 업데이트
    this._settings.fogEnabled = enabled;
    this._settings.fogType = type;
    this._settings.fogColor = color;
    this._settings.fogNear = near;
    this._settings.fogFar = far;
    this._settings.fogDensity = density;

    this.notifyEnvironmentChanged();
  }

  /**
   * 배경색 설정
   */
  public setBackgroundColor(color: string | number, intensity: number = 1.0): void {
    if (!this._scene) return;

    this._scene.background = new Color(color);
    this._scene.backgroundIntensity = intensity;

    this._settings.backgroundColor = color;
    this._settings.backgroundIntensity = intensity;

    this.notifyEnvironmentChanged();
  }

  /**
   * 그림자 설정
   */
  public setShadowSettings(
    enabled: boolean,
    type: 'PCFSoftShadowMap' | 'PCFShadowMap' | 'BasicShadowMap' = 'PCFSoftShadowMap',
    bias: number = -0.0001,
    normalBias: number = 0.02,
  ): void {
    this._settings.shadowEnabled = enabled;
    this._settings.shadowType = type;
    this._settings.shadowBias = bias;
    this._settings.shadowNormalBias = normalBias;

    // 모든 조명에 그림자 설정 적용
    this._lights.forEach((lightInfo) => {
      if (lightInfo.light.castShadow !== undefined) {
        lightInfo.light.castShadow = enabled;
      }
    });

    this.notifyEnvironmentChanged();
  }

  /**
   * 환경 설정 적용
   */
  public applyEnvironmentSettings(): void {
    if (!this._scene) return;

    // 안개 설정
    if (this._settings.fogEnabled) {
      this.setFog(
        true,
        this._settings.fogType,
        this._settings.fogColor,
        this._settings.fogNear,
        this._settings.fogFar,
        this._settings.fogDensity,
      );
    }

    // 배경색 설정
    this.setBackgroundColor(this._settings.backgroundColor, this._settings.backgroundIntensity);

    // 그림자 설정
    this.setShadowSettings(
      this._settings.shadowEnabled,
      this._settings.shadowType,
      this._settings.shadowBias,
      this._settings.shadowNormalBias,
    );

    // 바닥 설정
    if (this._settings.floorEnabled) {
      this.createFloor(
        this._settings.floorSize,
        this._settings.floorPosition,
        this._settings.floorColor,
        this._settings.floorRoughness,
        this._settings.floorMetalness,
      );
    }
  }

  /**
   * 환경 설정 가져오기
   */
  public getEnvironmentSettings(): EnvironmentSettings {
    return { ...this._settings };
  }

  /**
   * 환경 설정 저장
   */
  public saveEnvironmentSettings(): string {
    return JSON.stringify(this._settings, (key, value) => {
      if (value instanceof Vector3) {
        return { type: 'Vector3', x: value.x, y: value.y, z: value.z };
      }
      return value;
    });
  }

  /**
   * 환경 설정 복원
   */
  public loadEnvironmentSettings(settingsJson: string): boolean {
    try {
      const settings = JSON.parse(settingsJson, (key, value) => {
        if (value && value.type === 'Vector3') {
          return new Vector3(value.x, value.y, value.z);
        }
        return value;
      });

      this._settings = { ...this._settings, ...settings };
      this.applyEnvironmentSettings();
      return true;
    } catch (error) {
      console.error('환경 설정 로드 실패:', error);
      return false;
    }
  }

  /**
   * 모든 조명 정보 가져오기
   */
  public getAllLights(): LightInfo[] {
    return Array.from(this._lights.values());
  }

  /**
   * 조명 정보 가져오기
   */
  public getLight(lightId: string): LightInfo | undefined {
    return this._lights.get(lightId);
  }

  /**
   * 환경 그룹 가져오기
   */
  public getEnvironmentGroup(): Group {
    return this._environmentGroup;
  }

  /**
   * 조명 그룹 가져오기
   */
  public getLightsGroup(): Group {
    return this._lightsGroup;
  }

  /**
   * 바닥 그룹 가져오기
   */
  public getFloorGroup(): Group {
    return this._floorGroup;
  }

  /**
   * 이벤트 콜백 설정
   */
  public setOnEnvironmentChanged(callback: (settings: EnvironmentSettings) => void): void {
    this._onEnvironmentChanged = callback;
  }

  public setOnLightAdded(callback: (lightInfo: LightInfo) => void): void {
    this._onLightAdded = callback;
  }

  public setOnLightRemoved(callback: (lightId: string) => void): void {
    this._onLightRemoved = callback;
  }

  /**
   * 환경 변경 알림
   */
  private notifyEnvironmentChanged(): void {
    if (this._onEnvironmentChanged) {
      this._onEnvironmentChanged(this._settings);
    }
  }

  /**
   * 고유 조명 ID 생성
   */
  private generateLightId(): string {
    return `light_${++this._lightCounter}_${Date.now()}`;
  }

  /**
   * 모든 환경 요소 정리
   */
  public clearEnvironment(): void {
    // 조명 제거
    this._lights.forEach((lightInfo) => {
      this._lightsGroup.remove(lightInfo.light);
      lightInfo.light.dispose();
    });
    this._lights.clear();

    // 바닥 제거
    if (this._floorGroup) {
      this._floorGroup.clear();
    }

    // HDR 텍스처 정리
    if (this._hdrTexture) {
      this._hdrTexture.dispose();
      this._hdrTexture = null;
    }

    // 설정 초기화
    this._settings = this.getDefaultSettings();
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.clearEnvironment();

    if (this._scene) {
      this._scene.remove(this._environmentGroup);
    }

    this._scene = null;
    this._pmremGenerator = null;
  }
}
